import React, { useState, useEffect } from "react";
import { Table, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaEdit, FaTrash } from "react-icons/fa";
import authService from "../../../utils/authService";

interface BranchData {
  id: number;
  branch_id: string;
  branch: string;
  del_row: boolean;
  college_id_id: string | null;
}

interface CollegeData {
  college_id: string;
  college_name: string;
}

const Branches: React.FC = () => {
  const [search, setSearch] = useState("");
  const [branches, setBranches] = useState<BranchData[]>([]);
  const [colleges, setColleges] = useState<CollegeData[]>([]);
  const [selectedCollegeId, setSelectedCollegeId] = useState<string>("select");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("new");
  const [modalNewMessage, setModalNewMessage] = useState<string>("");
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<{
    branch_id: string;
    branch: string;
    college_id: string;
  }>({
    branch_id: "",
    branch: "",
    college_id: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedBranch, setSelectedBranch] = useState<BranchData | null>(null);

  useEffect(() => {
    fetchColleges();
  }, []);

  useEffect(() => {
    // Fetch branches only when a specific college is selected (not "select" or empty)
    if (selectedCollegeId && selectedCollegeId !== "select") {
      fetchBranches();
    } else {
      // Clear branches when "Select" is chosen
      setBranches([]);
    }
  }, [selectedCollegeId]);

  const fetchColleges = async () => {
    try {
      const response = await authService.get("/admin/colleges/");
      if (response.data && response.data.colleges) {
        setColleges(response.data.colleges as CollegeData[]);
      }
    } catch (error) {
      console.error("Error fetching colleges:", error);
    }
  };

  const fetchBranches = async () => {
    try {
      setLoading(true);
      setError("");
      // Send empty string for college_id when "All Colleges" is selected
      // Send actual college_id when a specific college is selected
      const collegeId = selectedCollegeId === "all" ? "" : selectedCollegeId;
      const response = await authService.post("/admin/branches/by_college/", {
        college_id: collegeId,
      });
      if (response.data && response.data.branches) {
        setBranches(response.data.branches as BranchData[]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setError("Failed to fetch branches");
      setLoading(false);
    }
  };

  const handleModal = (type: string, branch?: BranchData): void => {
    setModalType(type);
    if (type === "new") {
      setFormData({
        branch_id: "",
        branch: "",
        college_id: selectedCollegeId && selectedCollegeId !== "select" && selectedCollegeId !== "all" ? selectedCollegeId : "",
      });
    } else if (branch) {
      setFormData({
        branch_id: branch.branch_id,
        branch: branch.branch,
        college_id: branch.college_id_id || "",
      });
    }
    setShowModal(true);
    setError("");
  };

  const handleSave = async (): Promise<void> => {
    // Validation
    if (!formData.branch || !formData.college_id) {
      setError("All fields are required");
      return;
    }

    // Check for duplicate branch name within the same college (only for new branches)
    if (modalType === "new") {
      const isDuplicate = branches.some(
        (branch) =>
          branch.branch.toLowerCase() === formData.branch.toLowerCase() &&
          branch.college_id_id === formData.college_id &&
          !branch.del_row
      );
      if (isDuplicate) {
        setError("Branch name already exists for this college");
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      const json = {
        branch_id: modalType === "new" ? "" : formData.branch_id,
        branch: formData.branch.trim(),
        college_id: formData.college_id,
      };

      const response = await authService.post("/admin/add_branch/", json);

      if (response.status === 409) {
        setError("Branch name already exists for this college");
        setLoading(false);
        return;
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(`Failed to ${modalType === "edit" ? "update" : "save"} branch`);
      }

      // Refresh branches only if a college is selected
      if (selectedCollegeId && selectedCollegeId !== "select") {
        await fetchBranches();
      }

      setShowModal(false);
      setModalNewMessage(
        modalType === "edit" ? "Branch updated successfully" : "Branch created successfully"
      );
      setShowUpdateModal(true);
      setLoading(false);
    } catch (error: any) {
      if (error.response?.status === 409) {
        setError("Branch name already exists for this college");
      } else {
        setError(`Error ${modalType === "edit" ? "updating" : "saving"} branch`);
      }
      console.error(`Error ${modalType === "edit" ? "updating" : "saving"} branch:`, error);
      setLoading(false);
    }
  };

  const handleDelete = (branch: BranchData): void => {
    setSelectedBranch(branch);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (selectedBranch) {
      try {
        setLoading(true);
        const json = {
          branch_id: selectedBranch.branch_id,
        };
        const response = await authService.post("/admin/delete_branch/", json);

        if (response.status !== 200 && response.status !== 201) {
          throw new Error("Failed to delete branch");
        }

        // Refresh branches only if a college is selected
        if (selectedCollegeId && selectedCollegeId !== "select") {
          await fetchBranches();
        }

        setShowDeleteModal(false);
        setModalNewMessage("Branch deleted successfully");
        setShowUpdateModal(true);
        setLoading(false);
      } catch (error) {
        console.error("Error deleting branch:", error);
        setError("Failed to delete branch");
        setLoading(false);
      }
    }
  };

  const getCollegeName = (collegeId: string | null): string => {
    if (!collegeId) return "--";
    const college = colleges.find((c) => c.college_id === collegeId);
    return college ? college.college_name : collegeId;
  };

  const filteredBranches = branches.filter(
    (branch) =>
      !branch.del_row &&
      (branch.branch.toLowerCase().includes(search.toLowerCase()) ||
        getCollegeName(branch.college_id_id).toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div
      className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
      style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}
    >
      <div className="d-flex justify-content-between pe-3 align-items-center">
        <div className="d-flex justify-content-start align-items-center ps-4">
          <label className="me-2">Filter by College:</label>
          <Form.Select
            value={selectedCollegeId}
            onChange={(e) => setSelectedCollegeId(e.target.value)}
            style={{ width: "200px" }}
            className="me-3"
          >
            <option value="select">Select</option>
            <option value="all">All Colleges</option>
            {colleges.map((college) => (
              <option key={college.college_id} value={college.college_id}>
                {college.college_name}
              </option>
            ))}
          </Form.Select>
          <label className="me-2">Search</label>
          <Form.Control
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "150px" }}
            placeholder="Search branches..."
          />
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            if ((!selectedCollegeId || selectedCollegeId === "select" || selectedCollegeId === "all") && colleges.length > 0) {
              setError("Please select a specific college first");
              return;
            }
            handleModal("new");
          }}
          disabled={colleges.length === 0}
        >
          + New Branch
        </button>
      </div>
      <div className="p-4">
        {loading && branches.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
            <Spinner animation="border" />
          </div>
        ) : (
          <Table className="border-top mt-2">
            <thead
              style={{
                borderBottom: "1px solid black",
                borderTop: "1px solid black",
              }}
            >
              <tr>
                <td>Sl No</td>
                <td>Branch Name</td>
                <td>College</td>
                <td>Action</td>
              </tr>
            </thead>
            <tbody>
              {filteredBranches.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center">
                    {selectedCollegeId === "select"
                      ? "Please select a college to view branches"
                      : selectedCollegeId === "all"
                      ? "No branches found"
                      : "No branches found for selected college"}
                  </td>
                </tr>
              ) : (
                filteredBranches.map((branch, index) => (
                  <tr key={branch.branch_id}>
                    <td>{index + 1}</td>
                    <td>{branch.branch}</td>
                    <td>{getCollegeName(branch.college_id_id)}</td>
                    <td>
                      <FaEdit
                        className="fs-6 me-2"
                        role="button"
                        title="Edit"
                        onClick={() => {
                          handleModal("edit", branch);
                        }}
                        style={{ cursor: "pointer" }}
                      />
                      <FaTrash
                        className="fs-6 me-2"
                        title="Delete"
                        role="button"
                        onClick={() => handleDelete(branch)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the branch:{" "}
            <strong>{selectedBranch?.branch}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Yes, Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Branch Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === "new" ? "Add New Branch" : "Edit Branch"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {error && <p className="text-danger">{error}</p>}
          <Form>
            <Form.Group
              controlId="formCollege"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label className="me-2" style={{ minWidth: "150px" }}>
                College
              </Form.Label>
              <div style={{ width: "60%" }}>
                <Form.Select
                  value={formData.college_id}
                  onChange={(e) => {
                    setFormData({ ...formData, college_id: e.target.value });
                    setError("");
                  }}
                  disabled={modalType === "edit"}
                  required
                >
                  <option value="">Select College</option>
                  {colleges.map((college) => (
                    <option key={college.college_id} value={college.college_id}>
                      {college.college_name}
                    </option>
                  ))}
                </Form.Select>
                {modalType === "edit" && (
                  <Form.Text className="text-muted">
                    College cannot be changed when editing
                  </Form.Text>
                )}
              </div>
            </Form.Group>

            <Form.Group
              controlId="formBranchName"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label className="me-2" style={{ minWidth: "150px" }}>
                Branch Name
              </Form.Label>
              <div style={{ width: "60%" }}>
                <Form.Control
                  type="text"
                  name="branch"
                  value={formData.branch}
                  onChange={(e) => {
                    let value = e.target.value.toUpperCase();
                    // Only allow letters (A-Z), no numbers or special characters
                    value = value.replace(/[^A-Z]/g, "");
                    // Limit to 2 characters
                    if (value.length <= 2) {
                      setFormData({ ...formData, branch: value });
                      setError("");
                    }
                  }}
                  placeholder="Enter branch name (max 2 characters, letters only)"
                  maxLength={2}
                  isInvalid={formData.branch.length > 2}
                  required
                  style={{ textTransform: "uppercase" }}
                />
                <Form.Text className="text-muted">
                  {formData.branch.length}/2 characters (letters only)
                </Form.Text>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Message Modal */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalNewMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowUpdateModal(false)}>
            Okay
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Branches;

